import React from 'react';

const HelpCard: React.FC<{ title: string; objective: string; steps: string[] }> = ({ title, objective, steps }) => (
    <div className="bg-snapfy-surface rounded-xl p-6 border border-snapfy-border">
        <h3 className="text-xl font-bold text-snapfy-text-primary mb-3">{title}</h3>
        <div className="space-y-4">
            <div>
                <h4 className="font-semibold text-snapfy-accent-soft mb-2">O Objetivo</h4>
                <p className="text-snapfy-text-secondary">{objective}</p>
            </div>
            <div>
                <h4 className="font-semibold text-snapfy-accent-soft mb-2">Como Usar</h4>
                <ol className="list-decimal list-inside space-y-2 text-snapfy-text-secondary">
                    {steps.map((step, index) => (
                        <li key={index}>{step}</li>
                    ))}
                </ol>
            </div>
        </div>
    </div>
);

const HelpPage: React.FC = () => {
    return (
        <div className="max-w-4xl mx-auto space-y-12">
            <header>
                <h1 className="text-3xl sm:text-4xl font-bold text-snapfy-text-primary">Ajuda & Documentação</h1>
                <p className="text-lg text-snapfy-text-secondary mt-2">Encontre guias e explicações para aproveitar ao máximo a Snapfy.ai.</p>
            </header>

            <section>
                <h2 className="text-2xl font-semibold text-snapfy-text-primary mb-6 pb-3 border-b border-snapfy-border">Minha Área</h2>
                <div className="space-y-6">
                    <HelpCard
                        title="Galeria"
                        objective="Sua biblioteca central para todas as imagens e vídeos que você gerou. É o seu portfólio de criações, fácil de acessar, buscar e reutilizar."
                        steps={[
                            "Navegue por todas as suas criações em um único lugar.",
                            "Use a barra de busca para encontrar itens pelo prompt utilizado na geração.",
                            "Ordene sua galeria por data de criação ou em ordem alfabética pelo prompt.",
                            "Passe o mouse sobre uma imagem para ver as opções de Edição Rápida e Criação de Vídeo.",
                            "Clique em 'Utilizar prompt' para levar a descrição da imagem de volta à tela inicial e criar novas versões."
                        ]}
                    />
                    <HelpCard
                        title="Moodboard"
                        objective="Uma tela de criação livre para coletar ideias, criar conceitos visuais e gerar novas imagens a partir da combinação de referências."
                        steps={[
                            "Arraste e solte imagens do seu computador ou use o botão de upload para adicioná-las à tela.",
                            "Use a ferramenta de texto para adicionar notas, palavras-chave ou descrições.",
                            "Mova, redimensione e organize os itens livremente para criar sua composição visual.",
                            "Selecione as imagens e/ou textos que deseja usar e escreva um prompt para gerar uma nova imagem que combine esses elementos.",
                            "Clique em 'Salvar como Brandbook' para que a IA analise o conteúdo do seu moodboard e crie uma nova identidade de marca."
                        ]}
                    />
                    <HelpCard
                        title="Brandbook"
                        objective="Crie e gerencie identidades de marca. Um Brandbook captura o estilo fotográfico e a paleta de cores, garantindo consistência em todas as suas criações futuras."
                        steps={[
                            "Insira a URL de um site de e-commerce que tenha o estilo visual que você deseja replicar.",
                            "Clique em 'Analisar e Criar'. A IA irá estudar o site e extrair as informações de estilo.",
                            "Um novo card de Brandbook será criado, contendo um prompt de estilo, a paleta de cores e um moodboard visual gerado pela IA.",
                            "Use seus Brandbooks salvos nas telas 'Fashion' e 'Home & Decor' para aplicar um estilo consistente às suas imagens."
                        ]}
                    />
                </div>
            </section>

            <section>
                <h2 className="text-2xl font-semibold text-snapfy-text-primary mb-6 pb-3 border-b border-snapfy-border">Apps</h2>
                <div className="space-y-6">
                     <HelpCard
                        title="Fashion"
                        objective="A ferramenta ideal para criar fotos de e-commerce para peças de vestuário em grande escala, economizando tempo e recursos."
                        steps={[
                            "Defina o Estilo da Foto: Escolha um estilo pré-definido, crie um a partir de uma imagem de referência, URL, ou selecione um de seus Brandbooks salvos.",
                            "Envie as Peças: Faça o upload de todas as imagens das roupas que farão parte dos looks.",
                            "Escolha o Modelo: Selecione um modelo da nossa biblioteca ou descreva detalhadamente o perfil que você deseja.",
                            "Configure os Detalhes: Ajuste a proporção da imagem, o enquadramento (corpo inteiro, etc.) e o número de poses desejado para cada look.",
                            "Adicione à Fila: Combine as peças, o modelo e as configurações para montar um 'look' e adicioná-lo à fila de geração.",
                            "Gere Tudo: Após montar todos os looks, clique no botão principal para iniciar a geração de todas as imagens em lote."
                        ]}
                    />
                     <HelpCard
                        title="Home & Decor"
                        objective="Crie cenas e ambientes profissionais para seus produtos de casa e decoração, inserindo-os em cenários realistas."
                        steps={[
                            "Envie os Produtos: Faça o upload das imagens dos seus produtos (móveis, vasos, quadros, etc.).",
                            "Defina o Ambiente: Escolha uma das três opções para criar o cenário: descreva-o em texto, envie uma foto de um cômodo, ou aplique o estilo de um de seus Brandbooks.",
                            "Ajuste o Formato: Selecione a proporção da imagem final.",
                            "Adicione à Fila: Combine os produtos e o ambiente para criar uma 'composição' e adicioná-la à fila.",
                            "Gere as Composições: Clique no botão principal para gerar todas as cenas da sua fila de uma só vez."
                        ]}
                    />
                    <HelpCard
                        title="Editor"
                        objective="Para edições detalhadas e refinadas em uma única imagem, utilizando a técnica de in-painting (pintura digital)."
                        steps={[
                            "Envie a Imagem: Carregue a foto que você deseja editar.",
                            "Use o Pincel (Máscara): Pinte sobre a área da imagem que você quer modificar. Você pode ajustar o tamanho do pincel para maior precisão.",
                            "Descreva a Edição: No campo de prompt, explique detalhadamente o que você quer fazer na área marcada (ex: 'mude a cor para vermelho', 'adicione um relógio de ouro').",
                            "Adicione Referências (Opcional): Se desejar, envie até 3 imagens para guiar a IA no estilo ou no objeto a ser inserido.",
                            "Clique em Gerar: Inicie o processo para ver a sua imagem editada."
                        ]}
                    />
                </div>
            </section>
        </div>
    );
};

export default HelpPage;